import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";

const POSITIONS = ["PM", "QA", "DEV", "Content", "SEO", "Designer", ""];
const POS_COLORS = { PM: "#6366f1", QA: "#f59e0b", DEV: "#3b82f6", Content: "#10b981", SEO: "#8b5cf6", Designer: "#ec4899" };

export default function UsersView() {
  const { apiFetch, flash, dark, t, card, inp, btnP, btnG, btnSm } = useApp();

  const [tab, setTab] = useState("users"); // users | projects
  const [users, setUsers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editProjectId, setEditProjectId] = useState(null);
  const [editProjectForm, setEditProjectForm] = useState({ name: "", domain: "" });
  const [projectSearch, setProjectSearch] = useState("");

  // Form
  const [form, setForm] = useState({ username: "", password: "", full_name: "", position: "", role: "user", telegram_username: "" });

  const load = async () => {
    try {
      const [r, pr] = await Promise.all([
        apiFetch("/api/users"),
        apiFetch("/api/users/all-projects"),
      ]);
      const d = await r.json();
      const pd = await pr.json();
      if (Array.isArray(d)) setUsers(d);
      if (Array.isArray(pd)) setAllProjects(pd);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Gmail status
  useEffect(() => {
    apiFetch("/api/gmail/status").then(r => r.json()).then(d => setGmailConnected(d.connected)).catch(() => {});
    const handler = (e) => { if (e.data?.type === "GMAIL_CONNECTED") { setGmailConnected(true); flash("Gmail підключено!"); } };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const connectGmail = async () => {
    try {
      const r = await apiFetch("/api/gmail/auth-url");
      const d = await r.json();
      if (d.url) window.open(d.url, "gmail-auth", "width=600,height=700");
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const resetForm = () => { setForm({ username: "", password: "", full_name: "", position: "", role: "user", telegram_username: "" }); setShowAdd(false); setEditId(null); };

  const createUser = async () => {
    if (!form.username || !form.password) { flash("Логін і пароль обов'язкові"); return; }
    try {
      const r = await apiFetch("/api/users", { method: "POST", body: JSON.stringify(form) });
      const d = await r.json();
      if (d.error) { flash(d.error); return; }
      if (d.id) { setUsers(prev => [...prev, d]); resetForm(); flash("Користувач створений"); }
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const updateUser = async () => {
    if (!editId) return;
    try {
      const body = { ...form };
      if (!body.password) delete body.password;
      const r = await apiFetch(`/api/users/${editId}`, { method: "PUT", body: JSON.stringify(body) });
      const d = await r.json();
      if (d.id) { setUsers(prev => prev.map(u => u.id === d.id ? d : u)); resetForm(); flash("Збережено"); }
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const deleteUser = async (id) => {
    if (!confirm("Видалити користувача?")) return;
    try {
      const r = await apiFetch(`/api/users/${id}`, { method: "DELETE" });
      const d = await r.json();
      if (d.error) { flash(d.error); return; }
      setUsers(prev => prev.filter(u => u.id !== id));
      flash("Видалено");
    } catch {}
  };

  const startEdit = (u) => {
    setEditId(u.id);
    setForm({ username: u.username, password: "", full_name: u.full_name || "", position: u.position || "", role: u.role || "user", telegram_username: u.telegram_username || "" });
    setShowAdd(true);
  };

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>;

  const deleteProject = async (id) => {
    if (!window.confirm("Видалити проєкт? Всі дані будуть втрачені!")) return;
    try {
      await apiFetch(`/api/users/all-projects/${id}`, { method: "DELETE" });
      setAllProjects(prev => prev.filter(p => p.id !== id));
      flash("Проєкт видалено");
    } catch {}
  };

  const saveProject = async (id) => {
    try {
      await apiFetch(`/api/users/all-projects/${id}`, { method: "PUT", body: JSON.stringify(editProjectForm) });
      setAllProjects(prev => prev.map(p => p.id === id ? { ...p, ...editProjectForm } : p));
      setEditProjectId(null);
      flash("Збережено");
    } catch {}
  };

  const filteredProjects = allProjects.filter(p => {
    if (!projectSearch) return true;
    const q = projectSearch.toLowerCase();
    return (p.name || "").toLowerCase().includes(q) || (p.domain || "").toLowerCase().includes(q) || (p.owner_full_name || "").toLowerCase().includes(q);
  });

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 4 }}>
          {[
            { key: "users", label: `👥 Користувачі (${users.length})` },
            { key: "projects", label: `📁 Проєкти (${allProjects.length})` },
          ].map(tb => (
            <button key={tb.key} onClick={() => setTab(tb.key)} style={{
              ...btnG, fontSize: 12, padding: "6px 16px",
              background: tab === tb.key ? "#6366f1" : t.bgCard,
              color: tab === tb.key ? "#fff" : t.text,
              border: tab === tb.key ? "1px solid #6366f1" : `1px solid ${t.border}`,
              fontWeight: tab === tb.key ? 700 : 500,
            }}>{tb.label}</button>
          ))}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={connectGmail} style={{
            fontSize: 11, padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 600, border: "none",
            background: gmailConnected ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
            color: gmailConnected ? "#10b981" : "#ef4444",
          }}>{gmailConnected ? "✅ Gmail" : "📧 Gmail"}</button>
          {tab === "users" && <button onClick={() => { resetForm(); setShowAdd(true); }} style={{ ...btnP, fontSize: 12 }}>＋ Новий користувач</button>}
        </div>
      </div>

      {/* ═══ PROJECTS TAB ═══ */}
      {tab === "projects" && (
        <div>
          <input value={projectSearch} onChange={e => setProjectSearch(e.target.value)} placeholder="Пошук по назві, домену, власнику..."
            style={{ ...inp, fontSize: 12, padding: "8px 12px", marginBottom: 14 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {filteredProjects.map(p => {
              const isEditing = editProjectId === p.id;
              return (
                <div key={p.id} style={{ ...card, margin: 0, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: "linear-gradient(135deg,#6366f1,#a855f7)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                    {(p.name || "?")[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {isEditing ? (
                      <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                        <input value={editProjectForm.name} onChange={e => setEditProjectForm(f => ({ ...f, name: e.target.value }))} placeholder="Назва"
                          style={{ ...inp, fontSize: 12, padding: "4px 8px", flex: 1 }} />
                        <input value={editProjectForm.domain} onChange={e => setEditProjectForm(f => ({ ...f, domain: e.target.value }))} placeholder="Домен"
                          style={{ ...inp, fontSize: 12, padding: "4px 8px", flex: 1 }} />
                        <button onClick={() => saveProject(p.id)} style={{ ...btnP, fontSize: 11, padding: "4px 10px" }}>✓</button>
                        <button onClick={() => setEditProjectId(null)} style={{ ...btnG, fontSize: 11, padding: "4px 8px" }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</span>
                        {p.domain && p.domain !== p.name && <span style={{ fontSize: 11, color: "#6366f1" }}>{p.domain}</span>}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, fontSize: 10, color: t.textMuted }}>
                      <span>Власник: <strong style={{ color: t.text }}>{p.owner_full_name || p.owner_username?.split("@")[0] || "—"}</strong></span>
                      {p.payment && <span>Оплата: {p.payment}</span>}
                      {p.entity_type && <span>{p.entity_type}</span>}
                      <span>Кластерів: {p.cluster_count}</span>
                      <span>Створено: {new Date(p.created_at).toLocaleDateString("uk-UA")}</span>
                    </div>
                  </div>
                  {!isEditing && <button onClick={() => { setEditProjectId(p.id); setEditProjectForm({ name: p.name, domain: p.domain }); }} style={{ ...btnSm, fontSize: 11 }}>✏️</button>}
                  <button onClick={() => deleteProject(p.id)} style={{ ...btnSm, fontSize: 11, color: "#ef4444" }}>🗑</button>
                </div>
              );
            })}
            {filteredProjects.length === 0 && <div style={{ textAlign: "center", padding: 20, color: t.textMuted }}>Немає проєктів</div>}
          </div>
        </div>
      )}

      {/* ═══ USERS TAB ═══ */}
      {tab === "users" && <>

      {/* Add/Edit form */}
      {showAdd && (
        <div style={{ ...card, padding: 20, marginBottom: 16, border: `1px solid #6366f1` + "33" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{editId ? "Редагувати" : "Новий користувач"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600 }}>ПІБ</label>
              <input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                placeholder="Іванов Іван" style={{ ...inp, fontSize: 12, padding: "6px 10px" }} />
            </div>
            <div>
              <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600 }}>Посада</label>
              <select value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                style={{ ...inp, fontSize: 12, padding: "6px 10px" }}>
                <option value="">Не вказано</option>
                {POSITIONS.filter(Boolean).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600 }}>Логін (email)</label>
              <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                placeholder="user@company.com" style={{ ...inp, fontSize: 12, padding: "6px 10px" }} />
            </div>
            <div>
              <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600 }}>{editId ? "Новий пароль (пусто = без зміни)" : "Пароль"}</label>
              <div style={{ display: "flex", gap: 4 }}>
                <input type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder={editId ? "••••••" : "Пароль"} style={{ ...inp, fontSize: 12, padding: "6px 10px", flex: 1, fontFamily: "monospace" }} />
                <button type="button" onClick={() => {
                  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
                  let pwd = "";
                  const arr = new Uint32Array(14);
                  crypto.getRandomValues(arr);
                  for (let i = 0; i < 14; i++) pwd += chars[arr[i] % chars.length];
                  setForm(f => ({ ...f, password: pwd }));
                  if (navigator.clipboard) navigator.clipboard.writeText(pwd).catch(() => {});
                  flash("Пароль згенеровано та скопійовано");
                }} title="Згенерувати та скопіювати"
                  style={{ padding: "6px 10px", border: `1px solid ${t.border}`, background: "transparent", color: t.text, borderRadius: 6, fontSize: 14, cursor: "pointer" }}>🎲</button>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600 }}>Роль</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                style={{ ...inp, fontSize: 12, padding: "6px 10px" }}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="finance">Фінансист</option>
                <option value="dev">Dev (тільки дев-дошка)</option>
                <option value="ppc">PPC (рекламний кабінет)</option>
                <option value="copywriter">Копірайтер (кабінет текстів)</option>
                <option value="redactor">Редактор (перевірка текстів)</option>
                <option value="demo">Demo</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600 }}>Telegram</label>
              <input value={form.telegram_username} onChange={e => setForm(f => ({ ...f, telegram_username: e.target.value }))}
                placeholder="@username" style={{ ...inp, fontSize: 12, padding: "6px 10px" }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={editId ? updateUser : createUser} style={{ ...btnP, fontSize: 12 }}>{editId ? "Зберегти" : "Створити"}</button>
            <button onClick={resetForm} style={{ ...btnG, fontSize: 12 }}>Скасувати</button>
          </div>
        </div>
      )}

      {/* Users list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {users.map(u => {
          const posColor = POS_COLORS[u.position] || t.textMuted;
          return (
            <div key={u.id} style={{
              ...card, padding: "14px 18px", margin: 0, display: "flex", alignItems: "center", gap: 14,
            }}>
              {/* Avatar */}
              <div style={{
                width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                background: `linear-gradient(135deg, ${posColor}44, ${posColor}22)`,
                border: `1px solid ${posColor}33`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, fontWeight: 700, color: posColor,
              }}>
                {(u.full_name || u.username)[0]?.toUpperCase()}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{u.full_name || u.username.split("@")[0]}</span>
                  {u.position && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                      background: posColor + "15", color: posColor,
                    }}>{u.position}</span>
                  )}
                  {u.role === "admin" && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(239,68,68,0.1)", color: "#ef4444" }}>ADMIN</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                  {u.username}
                  {u.telegram_username && <span> · {u.telegram_username} {u.telegram_chat_id ? <span style={{ color: "#10b981", fontWeight: 600 }}>✅</span> : <span style={{ color: "#f59e0b" }}>⏳</span>}</span>}
                  {" · створений "}{new Date(u.created_at).toLocaleDateString("uk-UA")}
                </div>
              </div>

              {/* Actions */}
              <button onClick={() => startEdit(u)} style={{ ...btnSm, fontSize: 11 }}>✏️ Редагувати</button>
              <button onClick={() => deleteUser(u.id)} style={{ ...btnSm, fontSize: 11, color: "#ef4444" }}>🗑</button>
            </div>
          );
        })}
      </div>
      </>}
    </div>
  );
}
